import * as Cesium from 'cesium';

/**
 * Manages task rendering in Cesium 3D view
 */
export class TaskManager3D {
  constructor(viewer) {
    this.viewer = viewer;
    this.entities = []; // Store all task-related entities for cleanup
    this.DEFAULT_CYLINDER_HEIGHT = 3048; // 10,000 ft fallback in meters
  }
  
  /**
   * Clear all task entities from the scene
   */
  clearTask() {
    this.entities.forEach(entity => {
      this.viewer.entities.remove(entity);
    });
    this.entities = [];
  }
  
  /**
   * Draw a task on the 3D map
   * @param {Object} task - Task object with turnpoints array
   */
  drawTask(task) {
    // Clear any existing task
    this.clearTask();
    
    if (!task || !task.turnpoints || task.turnpoints.length === 0) {
      console.warn('Task has no turnpoints:', task);
      return;
    }
    
    console.log(`🗺️ Drawing task in 3D: ${task.task_name}`);
    
    const turnpoints = task.turnpoints;
    const topCoordinates = []; // Coordinates at the top of cylinders per turnpoint
    
    // Draw each turnpoint
    turnpoints.forEach((tp, index) => {
      const lon = parseFloat(tp.Lon);
      const lat = parseFloat(tp.Lat);
      const parseAlt = (v, fb) => {
        const n = parseFloat(v);
        return Number.isFinite(n) ? n : fb;
      };
      const minAlt = parseAlt(tp.MinAltMeters, 0);
      const maxAlt = parseAlt(tp.MaxAltMeters, minAlt + this.DEFAULT_CYLINDER_HEIGHT);
      const baseAlt = Math.min(minAlt, maxAlt);
      const topAlt = Math.max(minAlt, maxAlt);

      const groundPosition = Cesium.Cartesian3.fromDegrees(lon, lat, 0);
      const topPosition = Cesium.Cartesian3.fromDegrees(lon, lat, topAlt);
      topCoordinates.push(topPosition);
      
      // Draw center point at top (red circle marker)
      const centerMarker = this.viewer.entities.add({
        position: topPosition,
        point: {
          pixelSize: 12,
          color: Cesium.Color.fromCssColorString('#ef4444'),
          outlineColor: Cesium.Color.WHITE,
          outlineWidth: 2
        }
      });
      this.entities.push(centerMarker);
      
      // Draw ground circle to show the base on terrain
      if (tp.RadiusMeters) {
        const radius = parseFloat(tp.RadiusMeters);
        const groundCircle = this.viewer.entities.add({
          position: Cesium.Cartesian3.fromDegrees(lon, lat, 0),
          ellipse: {
            semiMinorAxis: radius,
            semiMajorAxis: radius,
            material: Cesium.Color.fromCssColorString('#94a3b8').withAlpha(0.4),
            heightReference: Cesium.HeightReference.CLAMP_TO_GROUND
          }
        });
        this.entities.push(groundCircle);
      }
      
      // Draw radius circle or sector based on angle
      if (tp.RadiusMeters) {
        const radius = parseFloat(tp.RadiusMeters);
        const angle = parseFloat(tp.Angle);
        
        // Calculate bearing to next turnpoint (or from previous if last)
        let bearing = Number.isFinite(Number(tp.bearing)) ? Number(tp.bearing) : 0;
        const isLastTurnpoint = index === turnpoints.length - 1;
        
        if (!Number.isFinite(Number(tp.bearing)) && index < turnpoints.length - 1) {
          // Bearing to next turnpoint
          const nextTp = turnpoints[index + 1];
          bearing = this.calculateBearing(tp.Lat, tp.Lon, nextTp.Lat, nextTp.Lon);
        } else if (!Number.isFinite(Number(tp.bearing)) && index > 0) {
          // For last turnpoint (finish), use bearing from previous
          const prevTp = turnpoints[index - 1];
          bearing = this.calculateBearing(prevTp.Lat, prevTp.Lon, tp.Lat, tp.Lon);
          
          // Reverse bearing for finish line (add 180 degrees)
          if (angle === 180) {
            bearing = (bearing + 180) % 360;
          }
        }
        
        // Create the sector/circle entity
        const sectorEntity = this.createSector(lat, lon, radius, angle, bearing, baseAlt, topAlt);
        if (sectorEntity) {
          this.entities.push(sectorEntity);
        }
      }
      
      // Add label for turnpoint name at the top
      const label = this.viewer.entities.add({
        position: topPosition,
        label: {
          text: tp.Name,
          font: '14px sans-serif',
          fillColor: Cesium.Color.WHITE,
          outlineColor: Cesium.Color.BLACK,
          outlineWidth: 2,
          style: Cesium.LabelStyle.FILL_AND_OUTLINE,
          verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
          pixelOffset: new Cesium.Cartesian2(0, -15),
          disableDepthTestDistance: Number.POSITIVE_INFINITY // Always visible
        }
      });
      this.entities.push(label);
    });
    
    // Draw connecting lines between turnpoints at the TOP of cylinders
    if (topCoordinates.length > 1) {
      const taskLine = this.viewer.entities.add({
        polyline: {
          positions: topCoordinates,
          width: 3,
          material: new Cesium.PolylineDashMaterialProperty({
            color: Cesium.Color.fromCssColorString('#3b82f6'),
            dashLength: 16.0,
            dashPattern: 255.0
          })
        }
      });
      this.entities.push(taskLine);
    }
    
    // Fit camera to show all turnpoints with cylinders
    if (topCoordinates.length > 0) {
      this.viewer.zoomTo(this.viewer.entities, new Cesium.HeadingPitchRange(0, Cesium.Math.toRadians(-45), 0));
    }
  }
  
  /**
   * Calculate bearing between two points in degrees
   */
  calculateBearing(lat1, lon1, lat2, lon2) {
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const lat1Rad = lat1 * Math.PI / 180;
    const lat2Rad = lat2 * Math.PI / 180;
    
    const y = Math.sin(dLon) * Math.cos(lat2Rad);
    const x = Math.cos(lat1Rad) * Math.sin(lat2Rad) -
              Math.sin(lat1Rad) * Math.cos(lat2Rad) * Math.cos(dLon);
    
    const bearing = Math.atan2(y, x) * 180 / Math.PI;
    return (bearing + 360) % 360; // Normalize to 0-360
  }
  
  /**
   * Create a sector (partial circle) or full circle for turnpoints as a 10,000ft tall cylinder
   * Uses extruded polygons for proper terrain clipping
   */
  createSector(lat, lon, radius, angle, bearing, baseAlt, topAlt) {
    const angleNum = parseFloat(angle);
    const center = { lat, lon };
    
    // Determine start and end angles based on sector type
    let startAngle, endAngle;
    
    if (angleNum === 360) {
      // Full circle - create a complete circular polygon
      startAngle = 0;
      endAngle = 360;
    } else if (angleNum === 180) {
      // Semicircle on the "back" side (opposite to the bearing direction)
      startAngle = bearing + 90;
      endAngle = bearing + 270;
    } else if (angleNum === 90) {
      // Quarter circle on the "outside corner"
      startAngle = bearing - 45;
      endAngle = bearing + 45;
    } else {
      // Default to full circle if unknown angle
      startAngle = 0;
      endAngle = 360;
    }
    
    // Generate points along the arc
    const positions = [Cesium.Cartographic.fromDegrees(lon, lat)];
    const numPoints = angleNum === 360 ? 64 : 50; // More points for full circles
    
    for (let i = 0; i <= numPoints; i++) {
      const angle = startAngle + (endAngle - startAngle) * (i / numPoints);
      const angleRad = angle * Math.PI / 180;
      
      // Calculate point on circle using destination point formula
      const lat1 = center.lat * Math.PI / 180;
      const lon1 = center.lon * Math.PI / 180;
      const angularDistance = radius / 6371000; // Earth radius in meters
      
      const lat2 = Math.asin(
        Math.sin(lat1) * Math.cos(angularDistance) +
        Math.cos(lat1) * Math.sin(angularDistance) * Math.cos(angleRad)
      );
      
      const lon2 = lon1 + Math.atan2(
        Math.sin(angleRad) * Math.sin(angularDistance) * Math.cos(lat1),
        Math.cos(angularDistance) - Math.sin(lat1) * Math.sin(lat2)
      );
      
      positions.push(Cesium.Cartographic.fromRadians(lon2, lat2));
    }
    
    // Close the sector (only if not a full circle)
    if (angleNum !== 360) {
      positions.push(Cesium.Cartographic.fromDegrees(lon, lat));
    }
    
    // Create extruded polygon (vertical cylinder/sector) from ground level
    // With depthTestAgainstTerrain enabled, this will be occluded by terrain
    const height = Math.min(baseAlt || 0, topAlt || this.DEFAULT_CYLINDER_HEIGHT);
    const extruded = Math.max(baseAlt || 0, topAlt || this.DEFAULT_CYLINDER_HEIGHT);
    return this.viewer.entities.add({
      polygon: {
        hierarchy: new Cesium.PolygonHierarchy(
          Cesium.Ellipsoid.WGS84.cartographicArrayToCartesianArray(positions)
        ),
        height: height, // meters MSL
        extrudedHeight: extruded, // meters MSL
        material: Cesium.Color.fromCssColorString('#94a3b8').withAlpha(0.3),
        outline: false, // Disable outline to avoid vertical edge lines
        closeTop: true,
        closeBottom: true
      }
    });
  }
  
  /**
   * Check if a task is currently displayed
   */
  hasTask() {
    return this.entities.length > 0;
  }
}
